import { CheckCircle, RefreshCw, XCircle } from 'lucide-react';
import React from 'react';
import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import api from '../../services/api';

const statusLabel = {
  PENDING: '승인 대기',
  MATCHED_EXISTING: '기존 RMC 매칭',
  APPROVED: '승인 완료',
  LINKED: 'SoftFlo 연결 완료',
  REJECTED: '반려',
};

const SoftFloRMCApplications = () => {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery(
    'softflo-rmc-applications',
    () => api.get('/super-admin/softflo-rmc-applications')
  );
  const applications = data?.applications || [];

  const approveMutation = useMutation(
    ({ id, payload }) => api.post(`/super-admin/softflo-rmc-applications/${id}/approve`, payload),
    {
      onSuccess: (result) => {
        queryClient.invalidateQueries('softflo-rmc-applications');
        queryClient.invalidateQueries('rental-management-companies');
        toast.success('SoftFlo 신청을 RentalHub RMC로 연결했습니다.');
        if (result?.login_info) {
          alert(`RMC 계정 생성 완료\n\n로그인 이메일: ${result.login_info.email}\n초기 비밀번호: ${result.login_info.password}`);
        }
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '승인 처리에 실패했습니다.');
      },
    }
  );

  const rejectMutation = useMutation(
    ({ id, reason }) => api.post(`/super-admin/softflo-rmc-applications/${id}/reject`, { rejection_reason: reason }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('softflo-rmc-applications');
        toast.success('신청을 반려했습니다.');
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || '반려 처리에 실패했습니다.');
      },
    }
  );

  const approve = (row) => {
    if (row.matched_rmc_id) {
      approveMutation.mutate({ id: row.id, payload: { mode: 'link_existing', rmc_id: row.matched_rmc_id } });
      return;
    }
    const initialPassword = window.prompt('신규 RMC 관리자 초기 비밀번호를 입력하세요. 비워두면 자동 생성됩니다.', '');
    approveMutation.mutate({
      id: row.id,
      payload: {
        mode: 'create_new',
        initial_password: initialPassword || undefined,
      },
    });
  };

  const reject = (row) => {
    const reason = window.prompt('반려 사유를 입력하세요.', '');
    if (reason === null) return;
    rejectMutation.mutate({ id: row.id, reason });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SoftFlo RMC 승인대기</h1>
          <p className="mt-1 text-sm text-gray-600">
            SoftFlo 무료체험 또는 유료결제 후 RentalHub 등록이 필요한 신청을 처리합니다.
          </p>
        </div>
        <button
          type="button"
          onClick={() => queryClient.invalidateQueries('softflo-rmc-applications')}
          className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw className="mr-2 h-4 w-4" />
          새로고침
        </button>
      </div>

      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">신청일</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">구분</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">상호명</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">사업자번호</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">신청자</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">상태</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">작업</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {isLoading ? (
              <tr><td className="px-4 py-8 text-center text-sm text-gray-500" colSpan="7">불러오는 중...</td></tr>
            ) : applications.length === 0 ? (
              <tr><td className="px-4 py-8 text-center text-sm text-gray-500" colSpan="7">승인대기 신청이 없습니다.</td></tr>
            ) : (
              applications.map((row) => (
                <tr key={row.id}>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{row.created_at?.slice(0, 16) || '-'}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{row.trial_flag ? '무료체험' : '유료결제'}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{row.organization_name}</td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm text-gray-700">{row.business_registration_no}</td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    <div>{row.applicant_name || row.representative_name || '-'}</div>
                    <div className="text-xs text-gray-500">{row.applicant_email || row.contact_email || '-'}</div>
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-sm">
                    <span className="rounded-full bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700">
                      {statusLabel[row.status] || row.status}
                    </span>
                    {row.matched_rmc_id && <div className="mt-1 text-xs text-gray-500">기존 RMC #{row.matched_rmc_id}</div>}
                    {row.confirm_status === 'FAILED' && <div className="mt-1 text-xs text-red-600">confirm 실패</div>}
                  </td>
                  <td className="whitespace-nowrap px-4 py-3 text-right text-sm">
                    {!['LINKED', 'APPROVED', 'REJECTED'].includes(row.status) && (
                      <div className="inline-flex gap-2">
                        <button
                          type="button"
                          onClick={() => approve(row)}
                          className="inline-flex items-center rounded-md bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700"
                        >
                          <CheckCircle className="mr-1 h-4 w-4" />
                          승인/연결
                        </button>
                        <button
                          type="button"
                          onClick={() => reject(row)}
                          className="inline-flex items-center rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
                        >
                          <XCircle className="mr-1 h-4 w-4" />
                          반려
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default SoftFloRMCApplications;
